import React, { useState, useEffect, useRef } from "react";
import { Dialog, Box, Typography, Button, IconButton, Paper } from "@mui/material";
import {
  Close,
  Stop,
  Refresh,
  Download,
  CheckCircle,
  Minimize,
  Maximize,
  DragIndicator,
} from "@mui/icons-material";
import Draggable from "react-draggable";
import { useStageRiskAnalysis } from "../../../hooks/useStageRiskAnalysis";
import {
  RISK_PHASES,
  PHASE_STATUS,
  exportRiskAnalysisLog,
} from "../../../utils/riskStreamingEventParser";
import useConfirm from "../../ui/useConfirm";

/* Palette: white + red/yellow/green for risk visualization */
const C = {
  bg: "#ffffff",
  surface: "#f8fafc",
  termBg: "#ffffff",
  border: "#e2e8f0",
  indigo: "#5664d2",
  indigoBg: "rgba(86,100,210,0.08)",
  indigoBdr: "rgba(86,100,210,0.25)",
  text: "#0f172a",
  sub: "#334155",
  dim: "#64748b",
  muted: "#94a3b8",
  error: "#dc2626",
  errBg: "rgba(220,38,38,0.06)",
  warning: "#f59e0b",
  warnBg: "rgba(245,158,11,0.06)",
  success: "#10b981",
  successBg: "rgba(16,185,129,0.06)",
  mono: '"Fira Code","Cascadia Code","Consolas",monospace',
};

const PHASE_NAMES = {
  1: "Task Collection",
  2: "Evidence Gathering",
  3: "Risk Analysis",
  4: "Error Detection",
  5: "Aggregation",
  6: "Recommendations",
};

/* Build terminal lines for risk analysis */
function buildRiskLines(
  phaseState,
  getPhaseData,
  _riskMetrics,
  _detectedRisks,
  _detectedErrors,
  _missingEvidence,
  _recommendations
) {
  const lines = [];

  Object.entries(RISK_PHASES).forEach(([sid, phase]) => {
    const id = parseInt(sid);
    const data = phaseState?.[id] || {};
    const status = data.status || PHASE_STATUS.PENDING;

    if (status === PHASE_STATUS.PENDING) {
      return;
    }

    lines.push({ id: `h${id}`, kind: "header", text: `▸ ${phase.name}` });

    const pd = getPhaseData(id)?.data || {};

    // Phase 1: Task Collection
    if (id === 1 && pd.total_tasks !== undefined) {
      // Log the actual data from backend for debugging

      lines.push({
        id: "tc",
        kind: "row",
        text: `  total tasks: ${pd.total_tasks}  (parents: ${pd.parent_tasks || 0}, subtasks: ${pd.child_tasks || 0})`,
      });
    }

    // Phase 1.5: Missing Task Detection & Coverage Analysis
    if (id === 1) {
      const phaseMissingTasks = phaseState[1]?.missingTasks || [];
      const phaseIncompleteCoverage = phaseState[1]?.incompleteCoverage || [];
      const coverageScore = phaseState[1]?.coverageScore;

      // Display missing tasks with criticality
      if (phaseMissingTasks.length > 0) {
        lines.push({ id: "missing_header", kind: "spacer" });
        phaseMissingTasks.forEach((missing, i) => {
          const critLabel = missing.criticality ? `[${missing.criticality.toUpperCase()}]` : "";
          const isCritical = missing.criticality === "critical" || missing.criticality === "high";
          lines.push({
            id: `missing_${i}`,
            kind: isCritical ? "error" : "warning",
            text: `    ⚠️  ${critLabel} MISSING: ${missing.requirement}`,
          });
          if (missing.description) {
            lines.push({
              id: `missing_desc_${i}`,
              kind: "dim",
              text: `       ${missing.description}`,
            });
          }
          if (missing.suggested_action) {
            lines.push({
              id: `missing_action_${i}`,
              kind: "dim",
              text: `       → ${missing.suggested_action}`,
            });
          }
        });
      }

      // Display incomplete coverage
      if (phaseIncompleteCoverage.length > 0) {
        lines.push({ id: "incomplete_header", kind: "spacer" });
        phaseIncompleteCoverage.forEach((incomplete, i) => {
          lines.push({
            id: `incomplete_${i}`,
            kind: "warning",
            text: `    📋  INCOMPLETE: ${incomplete.requirement}`,
          });
          if (incomplete.existing_task) {
            lines.push({
              id: `incomplete_task_${i}`,
              kind: "dim",
              text: `       existing: ${incomplete.existing_task}`,
            });
          }
          if (incomplete.gap) {
            lines.push({
              id: `incomplete_gap_${i}`,
              kind: "dim",
              text: `       gap: ${incomplete.gap}`,
            });
          }
        });
      }

      // Display coverage score
      if (coverageScore !== undefined && coverageScore !== null) {
        lines.push({ id: "coverage_spacer", kind: "spacer" });
        const scoreColor =
          coverageScore < 50 ? "error" : coverageScore < 80 ? "warning" : "success";
        lines.push({
          id: "coverage_score",
          kind: scoreColor,
          text: `  coverage score: ${coverageScore}%`,
        });
      }
    }

    // Phase 2: Evidence Gathering
    if (id === 2 && pd.task_id) {
      const attachCount = pd.attachments_found || 0;
      const commentCount = pd.comments_found || 0;
      lines.push({
        id: `ev_${pd.task_id}`,
        kind: "dim",
        text: `  ${pd.task_id}  attachments:${attachCount}  comments:${commentCount}`,
      });
    }

    // Phase 3: Risk Analysis
    if (id === 3) {
      // Show task risk progress
      if (pd.task_id && pd.risk_level) {
        const riskColor =
          pd.risk_level === "critical"
            ? "CRIT"
            : pd.risk_level === "high"
              ? "HIGH"
              : pd.risk_level === "medium"
                ? "MED"
                : "LOW";
        lines.push({
          id: `ra_${pd.task_id}`,
          kind: pd.risk_level === "critical" || pd.risk_level === "high" ? "warning" : "dim",
          text: `  ${pd.task_id}  risk:[${riskColor}]  score:${(pd.risk_score || 0).toFixed(1)}`,
        });
      }

      // Show detected risks
      const phaseRisks = phaseState[3]?.risksDetected || [];
      phaseRisks.forEach((risk, i) => {
        lines.push({
          id: `risk_${i}`,
          kind: "warning",
          text: `    ⚠️  [${risk.risk_level.toUpperCase()}] ${risk.task_id}: ${risk.description}`,
        });
      });
    }

    // Phase 4: Error Detection
    if (id === 4) {
      const phaseErrors = phaseState[4]?.errorsDetected || [];
      const phaseMissing = phaseState[4]?.missingEvidence || [];

      phaseErrors.forEach((err, i) => {
        lines.push({
          id: `err_${i}`,
          kind: "error",
          text: `    ❌  ${err.task_id}: ${err.description}`,
        });
      });

      phaseMissing.forEach((miss, i) => {
        lines.push({
          id: `miss_${i}`,
          kind: "warning",
          text: `    📋  ${miss.task_id} missing ${miss.evidence_type}: ${miss.description}`,
        });
      });
    }

    // Phase 5: Aggregation
    if (id === 5 && pd.stage_risk_score !== undefined) {
      lines.push({
        id: "agg",
        kind: "strong",
        text: `  stage health: ${pd.stage_health}  risk score: ${pd.stage_risk_score.toFixed(1)}/100`,
      });
      lines.push({
        id: "counts",
        kind: "dim",
        text: `  critical: ${pd.critical_count || 0}  high: ${pd.high_risk_count || 0}  errors: ${pd.error_count || 0}`,
      });
    }

    // Phase 6: Recommendations
    if (id === 6 && pd.recommendations && pd.recommendations.length > 0) {
      lines.push({
        id: "rec_count",
        kind: "row",
        text: `  generated ${pd.recommendations.length} recommendations`,
      });

      // Show ALL recommendations (removed .slice(0, 5) limitation)
      pd.recommendations.forEach((rec, i) => {
        const priority = rec.priority ? `[${rec.priority.toUpperCase()}]` : "";
        const priorityKind =
          rec.priority === "critical" ? "error" : rec.priority === "high" ? "warning" : "dim";

        lines.push({
          id: `rec_${i}`,
          kind: priorityKind,
          text: `    ${priority} ${rec.action || rec.description || "Recommendation"}`,
        });
      });
    }

    // Display message
    if (data.message) {
      data.message.split("\n").forEach((line, i) => {
        if (line.trim()) {
          lines.push({
            id: `m${id}_${i}`,
            kind: status === PHASE_STATUS.ERROR ? "error" : "dim",
            text: `  ${line}`,
          });
        }
      });
    }

    if (status === PHASE_STATUS.COMPLETE) {
      lines.push({ id: `ok${id}`, kind: "done", text: `  ✓ done` });
    }

    if (status === PHASE_STATUS.ERROR) {
      lines.push({ id: `stopped${id}`, kind: "error", text: `  ✗ stopped` });
    }

    lines.push({ id: `sp${id}`, kind: "spacer" });
  });

  return lines;
}

/* Terminal line component */
const Line = React.memo(({ item, isLast, analyzing }) => {
  if (item.kind === "spacer") {
    return <Box sx={{ height: 8 }} />;
  }

  if (item.kind === "header") {
    return (
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 700,
          color: C.indigo,
          fontFamily: C.mono,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          mt: 0.5,
          mb: 0.25,
          lineHeight: "18px",
        }}
      >
        {item.text}
      </Typography>
    );
  }

  const color =
    item.kind === "done"
      ? C.success
      : item.kind === "error"
        ? C.error
        : item.kind === "warning"
          ? C.warning
          : item.kind === "dim"
            ? C.dim
            : item.kind === "strong"
              ? C.text
              : C.sub;

  return (
    <Typography
      sx={{
        fontSize: "11.5px",
        fontFamily: C.mono,
        color,
        lineHeight: "19px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {item.text}
      {isLast && analyzing && (
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: 6,
            height: 12,
            bgcolor: C.indigo,
            ml: "2px",
            verticalAlign: "text-bottom",
            opacity: 0.7,
            animation: "bl 1s step-end infinite",
            "@keyframes bl": { "0%,100%": { opacity: 0.7 }, "50%": { opacity: 0 } },
          }}
        />
      )}
    </Typography>
  );
});

/* Phase timeline item */
const PhaseItem = ({ id, phaseData, isActive, isLast }) => {
  const status = phaseData?.status || PHASE_STATUS.PENDING;
  const isDone = status === PHASE_STATUS.COMPLETE;
  const isErr = status === PHASE_STATUS.ERROR;
  const dotColor = isErr ? C.error : isDone || isActive ? C.indigo : C.dim;

  return (
    <Box sx={{ display: "flex", gap: 1.25, minHeight: 36, alignItems: "flex-start" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 16,
          flexShrink: 0,
          pt: "4px",
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: isActive ? C.indigo : isDone ? C.indigo : "transparent",
            border: `1.5px solid ${dotColor}`,
            transition: "all 0.25s",
            boxShadow: isActive ? `0 0 0 3px ${C.indigoBg}` : "none",
            ...(isActive && {
              animation: "pd 1.4s ease-in-out infinite",
              "@keyframes pd": {
                "0%,100%": { boxShadow: `0 0 0 3px ${C.indigoBg}` },
                "50%": { boxShadow: `0 0 0 5px ${C.indigoBg}` },
              },
            }),
          }}
        />
        {!isLast && (
          <Box
            sx={{
              width: "1.5px",
              flex: 1,
              bgcolor: isDone ? C.indigoBdr : C.border,
              mt: "3px",
              minHeight: 18,
            }}
          />
        )}
      </Box>

      <Box sx={{ pt: "2px", pb: 1 }}>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: isActive || isDone ? 600 : 400,
            color: isActive ? C.indigo : isDone ? C.text : C.dim,
            lineHeight: 1.3,
            transition: "color 0.2s",
          }}
        >
          {PHASE_NAMES[id]}
        </Typography>
        {isActive && (
          <Typography sx={{ fontSize: "9.5px", color: C.indigo, fontFamily: C.mono, opacity: 0.8 }}>
            running
          </Typography>
        )}
        {isDone && (
          <Typography sx={{ fontSize: "9.5px", color: C.sub, fontFamily: C.mono }}>done</Typography>
        )}
      </Box>
    </Box>
  );
};

/* Risk metrics summary card */
const RiskMetricsCard = ({ metrics, isComplete }) => {
  if (!isComplete) {
    return null;
  }

  const healthColor =
    metrics.stage_health === "excellent" || metrics.stage_health === "good"
      ? C.success
      : metrics.stage_health === "fair" || metrics.stage_health === "moderate"
        ? C.warning
        : C.error;

  const scoreColor =
    metrics.stage_risk_score < 30 ? C.success : metrics.stage_risk_score < 60 ? C.warning : C.error;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: "8px",
        background: C.surface,
        border: `1px solid ${C.border}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: 700,
          color: C.dim,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          mb: 1.5,
        }}
      >
        Stage Risk Summary
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontSize: "9px", color: C.dim, mb: 0.3 }}>Health</Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: healthColor,
              textTransform: "capitalize",
            }}
          >
            {metrics.stage_health}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "9px", color: C.dim, mb: 0.3 }}>Risk Score</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: scoreColor }}>
            {metrics.stage_risk_score?.toFixed(1) || 0}/100
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "9px", color: C.dim, mb: 0.3 }}>Critical</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: C.error }}>
            {metrics.critical_count || 0}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "9px", color: C.dim, mb: 0.3 }}>High Risk</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: C.warning }}>
            {metrics.high_risk_count || 0}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "9px", color: C.dim, mb: 0.3 }}>Errors</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: C.error }}>
            {metrics.error_count || 0}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/* Draggable Paper Component */
function DraggablePaper(props) {
  const nodeRef = useRef(null);
  const { isMinimized, ...paperProps } = props;

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".drag-handle"
      cancel={'[class*="MuiDialogContent-root"]'}
      defaultPosition={{ x: 0, y: 0 }}
      key={isMinimized ? "minimized" : "maximized"}
    >
      <Paper ref={nodeRef} {...paperProps} />
    </Draggable>
  );
}

/* Main modal component */
const StageRiskAnalysisModal = ({
  modalKey: _modalKey,
  open,
  isMinimized = false,
  stackIndex = 0,
  onClose,
  onToggleMinimize = () => console.warn("[RiskModal] onToggleMinimize not provided"),
  workflowId,
  stageNumber,
  stageName = "Stage",
  missionName = "Workflow",
}) => {
  const [confirm, confirmDialog] = useConfirm();
  const logRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [webVal, setWebVal] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const [notificationSent, setNotificationSent] = useState(false);

  // Track the current workflow/stage to detect changes
  const [currentWorkflowId, setCurrentWorkflowId] = useState(null);
  const [currentStageNumber, setCurrentStageNumber] = useState(null);

  // Log mount/unmount
  useEffect(() => {
    return () => {};
  }, []);

  // Reset state when workflow or stage changes (new analysis started)
  useEffect(() => {
    if ((workflowId !== currentWorkflowId || stageNumber !== currentStageNumber) && open) {
      // New workflow/stage opened - reset everything
      setCurrentWorkflowId(workflowId);
      setCurrentStageNumber(stageNumber);
      // Note: isMinimized is managed by context, don't reset here
      setInternalOpen(true);
      setStarted(false);
      setNotificationSent(false); // Reset notification flag for new analysis
    }
  }, [workflowId, stageNumber, open, currentWorkflowId, currentStageNumber]);

  // Keep modal open when minimized, even if parent tries to close it
  useEffect(() => {
    // When minimized, completely ignore parent's open prop - we're independent
    if (isMinimized) {
      // Force modal to stay open when minimized, regardless of parent
      setInternalOpen(true);
      return;
    }
    // When not minimized, follow parent's open prop
    setInternalOpen(open);
  }, [open, isMinimized]);

  const {
    isAnalyzing,
    isConnected,
    error,
    phaseState,
    activePhase,
    overallProgress,
    allEvents,
    startTime,
    riskMetrics,
    recommendations,
    detectedRisks,
    detectedErrors,
    missingEvidence,
    startAnalysis,
    stopAnalysis,
    resetAnalysis,
    getPhaseData,
    isComplete,
    getFormattedDuration,
  } = useStageRiskAnalysis(workflowId, stageNumber);

  // Check if analysis is complete
  const done = isComplete();

  // Auto-start on open
  useEffect(() => {
    if (internalOpen && !started && !isAnalyzing) {
      setTimeout(() => {
        setStarted(true);
        startAnalysis("Analyze stage risks and errors", webVal);
      }, 400);
    }
  }, [internalOpen, started, isAnalyzing, webVal, startAnalysis]);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [phaseState, detectedRisks, detectedErrors, missingEvidence]);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Send notification when analysis completes
  useEffect(() => {
    if (done && started && !notificationSent) {
      if ("Notification" in window && Notification.permission === "granted") {
        const title = "Risk Analysis Complete";
        const body = `Stage ${stageNumber}: ${riskMetrics.stage_health || "Complete"} - ${riskMetrics.critical_count || 0} critical, ${riskMetrics.high_risk_count || 0} high risk`;

        const notification = new Notification(title, {
          body,
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          tag: `risk-analysis-${workflowId}-${stageNumber}`,
          requireInteraction: false,
          silent: false,
        });

        notification.onclick = () => {
          window.focus();
          if (isMinimized) {
            onToggleMinimize();
          }
          setInternalOpen(true);
          notification.close();
        };

        // Auto-close after 10 seconds
        setTimeout(() => notification.close(), 10000);

        setNotificationSent(true);
      } else if ("Notification" in window && Notification.permission !== "granted") {
      }
    }
  }, [
    done,
    started,
    notificationSent,
    stageNumber,
    workflowId,
    riskMetrics,
    isMinimized,
    onToggleMinimize,
  ]);

  const reset = () => {
    resetAnalysis();
    setStarted(false);
    setWebVal(false);
    setNotificationSent(false);
  };

  const close = async () => {
    if (isAnalyzing) {
      const ok = await confirm({
        title: "Analysis still running",
        description: "Closing now will stop the analysis in progress. Close anyway?",
        confirmText: "Close and stop",
        severity: "warning",
      });
      if (!ok) {
        return;
      }
    }
    if (isAnalyzing) {
      stopAnalysis();
    }
    setInternalOpen(false);
    // isMinimized is managed by context, will be reset when modal is removed
    onClose();
    reset();
  };

  const exportLog = () => {
    const blob = new Blob([exportRiskAnalysisLog(phaseState, allEvents)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: `stage-${stageNumber}-risk-analysis.txt`,
    });
    a.click();
    URL.revokeObjectURL(url);
  };

  const lines = buildRiskLines(
    phaseState,
    getPhaseData,
    riskMetrics,
    detectedRisks,
    detectedErrors,
    missingEvidence,
    recommendations
  );

  return (
    <Dialog
      open={internalOpen}
      onClose={(event, _reason) => {
        // Don't close when minimized - allow interaction with page
        if (isMinimized) {
          // Prevent any close attempts when minimized
          event?.stopPropagation();
          event?.preventDefault();
          return;
        }
        close();
      }}
      maxWidth="md"
      fullWidth
      PaperComponent={DraggablePaper}
      hideBackdrop={isMinimized}
      disableEnforceFocus={true}
      disableEscapeKeyDown={isMinimized}
      disableAutoFocus={true}
      disableRestoreFocus={true}
      disableScrollLock={true}
      keepMounted={true}
      sx={{
        pointerEvents: isMinimized ? "none" : undefined,
      }}
      PaperProps={{
        isMinimized,
        sx: {
          height: isMinimized ? "auto" : "82vh",
          maxHeight: isMinimized ? "auto" : "82vh",
          width: isMinimized ? "380px" : undefined,
          position: isMinimized ? "fixed" : undefined,
          bottom: isMinimized ? `${20 + stackIndex * 120}px` : undefined,
          right: isMinimized ? "20px" : undefined,
          top: isMinimized ? "auto" : undefined,
          left: isMinimized ? "auto" : undefined,
          margin: isMinimized ? "0" : undefined,
          borderRadius: "12px",
          border: `1px solid ${C.border}`,
          boxShadow: isMinimized
            ? "0 8px 24px rgba(15,23,42,0.2), 0 2px 8px rgba(15,23,42,0.1)"
            : "0 16px 48px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.06)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: C.bg,
          cursor: isMinimized ? "default" : undefined,
          transition: "all 0.3s ease",
          pointerEvents: "auto", // Ensure dialog itself is clickable
          zIndex: isMinimized ? 1300 : undefined, // Keep on top when minimized
        },
      }}
      BackdropProps={{
        sx: {
          background: isMinimized ? "transparent" : "rgba(15,23,42,0.35)",
          backdropFilter: isMinimized ? "none" : "blur(2px)",
          pointerEvents: "none", // Always allow clicking through backdrop
        },
      }}
      slotProps={{
        backdrop: {
          invisible: isMinimized,
        },
      }}
    >
      {/* HEADER */}
      <Box
        className="drag-handle"
        sx={{
          px: 2.5,
          py: 1.5,
          background: C.surface,
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          cursor: "move",
          userSelect: "none",
        }}
      >
        {/* Drag indicator */}
        <DragIndicator sx={{ fontSize: 16, color: C.muted, mr: -1 }} />

        {/* Dots - Red: close, Yellow: minimize/maximize, Green: maximize */}
        <Box sx={{ display: "flex", gap: "5px", mr: 0.5 }}>
          {["#f85149", "#d29922", "#3fb950"].map((c, i) => (
            <Box
              key={i}
              onClick={
                i === 0
                  ? close
                  : i === 1
                    ? () => {
                        onToggleMinimize();
                        setInternalOpen(true);
                      }
                    : i === 2
                      ? () => {
                          onToggleMinimize();
                          setInternalOpen(true);
                        }
                      : undefined
              }
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: c,
                cursor: i === 0 || i === 1 || i === 2 ? "pointer" : "default",
                "&:hover": i === 0 || i === 1 || i === 2 ? { filter: "brightness(0.8)" } : {},
              }}
            />
          ))}
        </Box>

        {/* Breadcrumb */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontSize: "11px", color: C.dim, fontFamily: C.mono }}>
              {missionName}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: C.dim }}>›</Typography>
            <Typography
              sx={{ fontSize: "11px", color: C.indigo, fontFamily: C.mono, fontWeight: 600 }}
            >
              stage-{stageNumber}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: C.dim }}>·</Typography>
            <Typography sx={{ fontSize: "11px", color: C.sub, fontFamily: C.mono }}>
              {stageName}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: C.dim }}>·</Typography>
            <Typography sx={{ fontSize: "11px", color: C.error, fontFamily: C.mono }}>
              risk analysis
            </Typography>
          </Box>
        </Box>

        {/* Status pill */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {started && (
            <Typography sx={{ fontSize: "10px", color: C.sub, fontFamily: C.mono }}>
              {overallProgress}%{startTime ? `  ·  ${getFormattedDuration()}` : ""}
            </Typography>
          )}
          {isAnalyzing && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.25,
                py: 0.35,
                borderRadius: "20px",
                background: C.indigoBg,
                border: `1px solid ${C.indigoBdr}`,
              }}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  bgcolor: C.indigo,
                  animation: "pd 1.4s ease-in-out infinite",
                  "@keyframes pd": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                }}
              />
              <Typography
                sx={{ fontSize: "10px", color: C.indigo, fontWeight: 600, fontFamily: C.mono }}
              >
                analyzing
              </Typography>
            </Box>
          )}
          {done && !isAnalyzing && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.25,
                py: 0.35,
                borderRadius: "20px",
                background: C.successBg,
                border: `1px solid ${C.success}`,
              }}
            >
              <CheckCircle sx={{ fontSize: 10, color: C.success }} />
              <Typography
                sx={{ fontSize: "10px", color: C.success, fontWeight: 600, fontFamily: C.mono }}
              >
                complete
              </Typography>
            </Box>
          )}
        </Box>

        {/* Window controls */}
        <IconButton
          onClick={() => {
            onToggleMinimize();
            setInternalOpen(true);
          }}
          size="small"
          sx={{ color: C.sub, p: 0.5, borderRadius: "6px", "&:hover": { bgcolor: "grey.100" } }}
          title={isMinimized ? "Maximize" : "Minimize"}
        >
          {isMinimized ? <Maximize sx={{ fontSize: 15 }} /> : <Minimize sx={{ fontSize: 15 }} />}
        </IconButton>

        <IconButton
          onClick={close}
          size="small"
          sx={{ color: C.sub, p: 0.5, borderRadius: "6px", "&:hover": { bgcolor: "grey.100" } }}
          title="Close"
        >
          <Close sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* PROGRESS RAIL */}
      {started && !isMinimized && (
        <Box
          sx={{
            height: 2,
            bgcolor: C.border,
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${overallProgress}%`,
              bgcolor: C.indigo,
              transition: "width 0.4s ease",
              boxShadow: `0 0 8px ${C.indigoBdr}`,
            }}
          />
        </Box>
      )}

      {/* Minimized progress indicator */}
      {isMinimized && (
        <Box sx={{ px: 2.5, py: 1.5, background: C.bg }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75 }}>
            {isAnalyzing && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: C.indigo,
                  flexShrink: 0,
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.5, transform: "scale(0.9)" },
                  },
                }}
              />
            )}
            <Typography sx={{ fontSize: "11px", color: C.sub, fontFamily: C.mono, flex: 1 }}>
              {isAnalyzing ? `Analyzing... ${overallProgress}%` : "Analysis complete"}
            </Typography>
            {activePhase && (
              <Typography sx={{ fontSize: "10px", color: C.dim, fontFamily: C.mono }}>
                Phase {activePhase}/6
              </Typography>
            )}
          </Box>

          {/* Mini progress bar */}
          <Box sx={{ height: 3, bgcolor: C.border, borderRadius: 2, overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                width: `${overallProgress}%`,
                bgcolor: C.indigo,
                transition: "width 0.4s ease",
              }}
            />
          </Box>

          {/* Quick stats */}
          {done && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                gap: 1.5,
                fontSize: "10px",
                color: C.dim,
                fontFamily: C.mono,
              }}
            >
              <span>Risks: {riskMetrics.critical_count + riskMetrics.high_risk_count}</span>
              <span>·</span>
              <span>Errors: {riskMetrics.error_count}</span>
              <span>·</span>
              <span
                style={{ color: riskMetrics.stage_health === "critical" ? C.error : C.success }}
              >
                {riskMetrics.stage_health}
              </span>
            </Box>
          )}
        </Box>
      )}

      {/* BODY */}
      {!isMinimized && (
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Phase timeline sidebar */}
          <Box
            sx={{
              width: 160,
              flexShrink: 0,
              borderRight: `1px solid ${C.border}`,
              background: C.surface,
              px: 2,
              pt: 2,
              pb: 1.5,
              overflowY: "auto",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Typography
              sx={{
                fontSize: "9px",
                fontWeight: 700,
                color: C.dim,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                mb: 1.5,
              }}
            >
              Phases
            </Typography>

            {Object.keys(RISK_PHASES).map((sid, idx, arr) => (
              <PhaseItem
                key={sid}
                id={parseInt(sid)}
                phaseData={phaseState?.[parseInt(sid)]}
                isActive={activePhase === parseInt(sid)}
                isLast={idx === arr.length - 1}
              />
            ))}

            {/* Duration */}
            {startTime && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${C.border}` }}>
                <Typography sx={{ fontSize: "9px", color: C.dim, fontFamily: C.mono }}>
                  elapsed
                </Typography>
                <Typography
                  sx={{ fontSize: "11px", color: C.sub, fontFamily: C.mono, fontWeight: 600 }}
                >
                  {getFormattedDuration()}
                </Typography>
              </Box>
            )}

            {/* Risk metrics summary */}
            <RiskMetricsCard metrics={riskMetrics} isComplete={done} />
          </Box>

          {/* Terminal log */}
          <Box
            ref={logRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              background: C.termBg,
              px: 2.5,
              py: 2,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: C.indigoBdr, borderRadius: 2 },
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${C.border}` }}>
              <Typography sx={{ fontSize: "10px", color: C.dim, fontFamily: C.mono, mb: 0.3 }}>
                $ certbuddy analyze-risks stage-{stageNumber}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: C.sub,
                  fontFamily: C.mono,
                  pl: 1,
                  borderLeft: `2px solid ${C.border}`,
                }}
              >
                Analyzing all tasks, evidence, and compliance risks
              </Typography>
            </Box>

            {/* Status */}
            {isAnalyzing && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: C.indigo,
                    boxShadow: `0 0 5px ${C.indigoBdr}`,
                  }}
                />
                <Typography sx={{ fontSize: "10.5px", color: C.indigo, fontFamily: C.mono }}>
                  {isConnected ? "streaming" : "connecting…"}
                  {activePhase ? `  ·  phase ${activePhase}` : ""}
                </Typography>
              </Box>
            )}

            {/* Error */}
            {error && (
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: "6px",
                  mb: 1.5,
                  background: C.errBg,
                  border: `1px solid rgba(220,38,38,0.15)`,
                }}
              >
                <Typography sx={{ fontSize: "11px", color: C.error, fontFamily: C.mono }}>
                  ✗ {error}
                </Typography>
              </Box>
            )}

            {/* Lines */}
            {lines.map((item, i) => (
              <Line
                key={item.id}
                item={item}
                isLast={i === lines.length - 1}
                analyzing={isAnalyzing}
              />
            ))}

            {isAnalyzing && lines.length === 0 && (
              <Typography sx={{ fontSize: "11px", color: C.dim, fontFamily: C.mono }}>
                initializing…
              </Typography>
            )}

            {/* Done */}
            {done && (
              <Box
                sx={{
                  mt: 2,
                  pt: 1.5,
                  borderTop: `1px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CheckCircle sx={{ fontSize: 13, color: C.success }} />
                <Typography
                  sx={{ fontSize: "11px", color: C.success, fontFamily: C.mono, fontWeight: 600 }}
                >
                  analysis complete · {getFormattedDuration()}
                </Typography>
              </Box>
            )}

            {/* Cursor */}
            {isAnalyzing && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 12,
                    bgcolor: C.indigo,
                    opacity: 0.5,
                    animation: "bl 1s step-end infinite",
                    "@keyframes bl": { "0%,100%": { opacity: 0.5 }, "50%": { opacity: 0 } },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* FOOTER */}
      {!isMinimized && (
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: `1px solid ${C.border}`,
            background: C.surface,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {started && (
            <Button
              startIcon={<Download sx={{ fontSize: 13 }} />}
              onClick={exportLog}
              size="small"
              sx={btn()}
            >
              Export
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {started && !isAnalyzing && (
            <Button
              startIcon={<Refresh sx={{ fontSize: 13 }} />}
              onClick={reset}
              size="small"
              sx={btn()}
            >
              Reset
            </Button>
          )}
          {isAnalyzing && (
            <Button
              startIcon={<Stop sx={{ fontSize: 13 }} />}
              onClick={stopAnalysis}
              size="small"
              sx={btn({ color: C.error, border: "rgba(220,38,38,0.25)", hoverBg: C.errBg })}
            >
              Stop
            </Button>
          )}
          <Button onClick={close} size="small" sx={btn()}>
            Close
          </Button>
        </Box>
      )}
      {confirmDialog}
    </Dialog>
  );
};

function btn(opts = {}) {
  const { color = "#64748b", border = "#e2e8f0", hoverBg = "#f1f5f9" } = opts;
  return {
    textTransform: "none",
    fontSize: "11.5px",
    fontWeight: 600,
    color,
    border: `1px solid ${border}`,
    borderRadius: "6px",
    px: 1.5,
    py: 0.6,
    "&:hover": { bgcolor: hoverBg },
  };
}

export default StageRiskAnalysisModal;
